import {
  AgendaExame,
  AgendaGradeLinha,
  AgendaTipo,
  AgendaTransferirLoteRequest,
  AgendaTransferirRequest,
  AgendaVaga,
  AgendaVagas,
  AgendaVagasUnidade,
} from '../../../../interfaces/agenda';
import { tipoAgendamento } from '../agenda-acoes';
import { somarDias } from '../agenda-filtro';

/** 21 dias em 3 chamadas de 7: uma busca só de 21 dias segura uma conexão por tempo demais. */
export const SEMANAS_BUSCA = 3;
export const DIAS_POR_BUSCA = 7;

export interface AgendaJanela {
  dtInicio: string;
  dtFim: string;
}

export function janelasBusca(hoje: string): AgendaJanela[] {
  return Array.from({ length: SEMANAS_BUSCA }, (_, s) => {
    const dtInicio = somarDias(hoje, s * DIAS_POR_BUSCA);
    return { dtInicio, dtFim: somarDias(dtInicio, DIAS_POR_BUSCA - 1) };
  });
}

/** Odonto mora em AGENDA_CONSULTA, mas a busca de vagas e os filtros só o enxergam pedindo a área. */
export function ehOdonto(l: AgendaGradeLinha): boolean {
  return tipoAgendamento(l) === 'CONSULTA' && [1, 2].includes(Number(l.nrSeqAreaAtuacao));
}

export function tipoBuscaVagas(l: AgendaGradeLinha): AgendaTipo {
  if (tipoAgendamento(l) === 'EXAME') return 'EXAME';
  return ehOdonto(l) ? 'ODONTOLOGIA' : 'CONSULTA';
}

/** Junta as unidades das semanas (uma janela que falhou chega como null). */
export function mesclarVagas(respostas: (AgendaVagas | null)[]): AgendaVagasUnidade[] {
  const porUnidade = new Map<number, AgendaVagasUnidade>();
  for (const r of respostas) {
    for (const u of r?.unidades ?? []) {
      const atual = porUnidade.get(u.cdEstabelecimento);
      if (atual) atual.horarios.push(...u.horarios);
      else porUnidade.set(u.cdEstabelecimento, { ...u, horarios: [...u.horarios] });
    }
  }
  return [...porUnidade.values()];
}

export interface AgendaVagaDestino extends AgendaVaga {
  cdEstabelecimento: number;
  nmEstabelecimento: string | null;
}

export interface AgendaDestino {
  cdAgenda: number;
  dsAgenda: string | null;
  /** Titular da agenda (AGENDA.CD_PESSOA_FISICA): é ele que decide se o médico muda. */
  cdMedico: string | null;
  nmMedico: string | null;
  dsEspecialidade: string | null;
  cdEstabelecimento: number;
  nmEstabelecimento: string | null;
  vagas: AgendaVagaDestino[];
}

export interface AgendaDiaVagas {
  data: string;
  agendas: AgendaDestino[];
}

const hora = (v: { hrInicio: string | null }) => v.hrInicio ?? '';

/**
 * Vagas por dia e por agenda. Os horários de hoje que já passaram não servem para a remarcação
 * individual (`ocultarPassados`); o lote os mantém porque o back preenche a partir do primeiro
 * livre do dia, passado ou não.
 */
export function vagasPorDia(unidades: AgendaVagasUnidade[], hoje: string, agora: string, ocultarPassados: boolean): AgendaDiaVagas[] {
  const dias = new Map<string, Map<number, AgendaDestino>>();
  for (const u of unidades) {
    for (const h of u.horarios) {
      const data = (h.dtDisponivel ?? '').slice(0, 10);
      if (!data) continue;
      if (ocultarPassados && data === hoje && hora(h) <= agora) continue;
      if (ocultarPassados && data < hoje) continue;
      let agendas = dias.get(data);
      if (!agendas) dias.set(data, (agendas = new Map()));
      let ag = agendas.get(h.cdAgenda);
      if (!ag) {
        ag = {
          cdAgenda: h.cdAgenda, dsAgenda: h.dsAgenda, cdMedico: h.cdMedico, nmMedico: h.nmMedico,
          dsEspecialidade: h.dsEspecialidade, cdEstabelecimento: u.cdEstabelecimento,
          nmEstabelecimento: u.nmEstabelecimento, vagas: [],
        };
        agendas.set(h.cdAgenda, ag);
      }
      ag.vagas.push({ ...h, cdEstabelecimento: u.cdEstabelecimento, nmEstabelecimento: u.nmEstabelecimento });
    }
  }
  return [...dias.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([data, agendas]) => ({
      data,
      agendas: [...agendas.values()]
        .map(a => ({ ...a, vagas: a.vagas.sort((x, y) => hora(x).localeCompare(hora(y))) }))
        .sort((a, b) => (a.nmMedico || a.dsAgenda || '').localeCompare(b.nmMedico || b.dsAgenda || '')),
    }));
}

export function diasDaJanela(dias: AgendaDiaVagas[], j: AgendaJanela): AgendaDiaVagas[] {
  return dias.filter(d => d.data >= j.dtInicio && d.data <= j.dtFim);
}

export interface AgendaMedicoResultante {
  cd: string;
  nm: string;
  trocou: boolean;
}

/**
 * Espelho de MedicoNoDestino do back. Compara os TITULARES das duas agendas, por código: numa
 * agenda compartilhada o agendamento tem o médico real e a agenda outro dono, e comparar o médico
 * do agendamento com o titular anunciaria uma troca que o servidor não faz.
 */
export function medicoResultante(
  origem: AgendaGradeLinha,
  destino: { cdMedico: string | null; nmMedico: string | null },
): AgendaMedicoResultante {
  const titularOrigem = String(origem.medicoAgenda?.cdMedico ?? '');
  const titularDestino = String(destino.cdMedico ?? '');
  const doAgendamento = String(origem.medico?.cdMedico ?? '');
  if (titularDestino && titularOrigem && titularDestino !== titularOrigem) {
    return { cd: titularDestino, nm: destino.nmMedico ?? '', trocou: !doAgendamento || doAgendamento !== titularDestino };
  }
  return { cd: doAgendamento, nm: origem.medico?.nmMedico ?? '', trocou: false };
}

/** Quem atende hoje o agendamento: o médico dele, senão o titular. */
export function medicoAtual(l: AgendaGradeLinha): string {
  return l.medico?.nmMedico || l.medicoAgenda?.nmMedico || '';
}

/** O lote só ocupa 'L' (o reservado da supervisão não entra), em ordem de hora. */
export function livresDoLote(a: AgendaDestino): AgendaVagaDestino[] {
  return a.vagas.filter(v => v.ieStatusAgenda === 'L');
}

export interface AgendaItemLote {
  origem: AgendaGradeLinha;
  destino: AgendaVagaDestino | null;
}

/** Mesma distribuição do back: i-ésima origem (em ordem de horário) no i-ésimo livre. */
export function distribuirLote(origens: AgendaGradeLinha[], livres: AgendaVagaDestino[]): AgendaItemLote[] {
  return origens.map((origem, i) => ({ origem, destino: livres[i] ?? null }));
}

export function corpoTransferir(origem: AgendaGradeLinha, destino: AgendaVaga, cdCanal: number): AgendaTransferirRequest {
  const tipo = tipoAgendamento(origem);
  return { nrSequenciaOrigem: origem.nrSequencia, tipoOrigem: tipo, nrSequenciaDestino: destino.id, tipoDestino: tipo, cdCanal };
}

export function corpoTransferirLote(origens: AgendaGradeLinha[], cdAgendaDestino: number, dtAgendaDestino: string): AgendaTransferirLoteRequest {
  return {
    nrSequenciasOrigem: origens.map(o => o.nrSequencia),
    tipo: tipoAgendamento(origens[0]),
    cdAgendaDestino,
    dtAgendaDestino,
  };
}

/**
 * O lote é tudo ou nada e a recusa vem só com a mensagem do item que travou. Ela traz o
 * NR_SEQUENCIA ("Agendamento 123 (07:00)...", "Falha ao preencher destino para 123 (07:00)") ou,
 * na duplicidade de exame, só a hora; a hora só identifica quando um único item a tem.
 */
export function itemQueBloqueou(mensagem: string, origens: AgendaGradeLinha[]): AgendaGradeLinha | null {
  const porNumero = /(\d{4,})\s*\((\d{2}:\d{2})\)/.exec(mensagem);
  if (porNumero) {
    const achado = origens.find(o => String(o.nrSequencia) === porNumero[1]);
    if (achado) return achado;
  }
  const porHora = /\((\d{2}:\d{2})\)/.exec(mensagem);
  if (!porHora) return null;
  const mesmaHora = origens.filter(o => o.hrInicio === porHora[1]);
  return mesmaHora.length === 1 ? mesmaHora[0] : null;
}

export interface AgendaExameAgrupado {
  nome: string;
  parceiros: AgendaExame[];
  semValor: boolean;
}

export function agruparExames(lista: AgendaExame[]): AgendaExameAgrupado[] {
  const grupos = new Map<string, AgendaExame[]>();
  for (const e of lista) {
    const nome = e.dsProcExame ?? '';
    if (!grupos.has(nome)) grupos.set(nome, []);
    grupos.get(nome)!.push(e);
  }
  return [...grupos.entries()].map(([nome, parceiros]) => ({
    nome, parceiros, semValor: parceiros.every(p => p.ieTemValor === 'N'),
  }));
}

export interface AgendaOpcaoSimples {
  codigo: string;
  descricao: string;
}

export interface AgendaCascataExame {
  medicos: AgendaOpcaoSimples[];
  unidades: AgendaOpcaoSimples[];
  /** Escolhas que continuam valendo depois de recalcular (a que sumiu volta para "Todos"). */
  cdMedico: string | null;
  cdEstabelecimento: string | null;
  cdAgendas: number[];
}

function opcoesUnicas(lista: AgendaExame[], cd: (e: AgendaExame) => unknown, nm: (e: AgendaExame) => string | null): AgendaOpcaoSimples[] {
  const m = new Map<string, string>();
  for (const e of lista) {
    const c = cd(e);
    if (c === null || c === undefined || c === '') continue;
    if (!m.has(String(c))) m.set(String(c), nm(e) ?? '');
  }
  return [...m.entries()].map(([codigo, descricao]) => ({ codigo, descricao })).sort((a, b) => a.descricao.localeCompare(b.descricao));
}

/** Exame → Médico → Unidade, tudo a partir dos parceiros do exame escolhido (como o original). */
export function cascataExame(parceiros: AgendaExame[], cdMedico: string | null, cdEstabelecimento: string | null): AgendaCascataExame {
  const medicos = opcoesUnicas(parceiros, e => e.cdMedico, e => e.nmMedico);
  const med = cdMedico && medicos.some(m => m.codigo === cdMedico) ? cdMedico : null;
  const doMedico = med ? parceiros.filter(p => String(p.cdMedico) === med) : parceiros;
  const unidades = opcoesUnicas(doMedico, e => e.cdEstabelecimento, e => e.nmEstabelecimento);
  const est = cdEstabelecimento && unidades.some(u => u.codigo === cdEstabelecimento) ? cdEstabelecimento : null;
  const filtrados = est ? doMedico.filter(p => String(p.cdEstabelecimento) === est) : doMedico;
  const cdAgendas = [...new Set(filtrados.map(p => p.cdAgenda).filter((c): c is number => c !== null && c !== undefined))];
  return { medicos, unidades, cdMedico: med, cdEstabelecimento: est, cdAgendas };
}

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export function rotuloDia(ds: string): string {
  const [a, m, d] = ds.slice(0, 10).split('-').map(Number);
  if (!a) return '';
  return `${DIAS_SEMANA[new Date(a, m - 1, d).getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

export function dataCurta(ds: string): string {
  const [, m, d] = ds.slice(0, 10).split('-');
  return d ? `${d}/${m}` : '';
}
